/* =====================================================================
   hzApi.ts - the dashboard's data layer.

   Replaces the in-browser sample store. Every screen of HospitalityManagement.tsx still reads and writes the same
   shared store through useHzData(); what changed is where the data lives:

     load    GET  {base}/api/dashboard/store              -> every screen's rows, straight from the database views
     save    POST {base}/api/dashboard/store/commit       -> the screens' create / update / delete operations

   A screen keeps calling setData(...) exactly as before. The store updates at once (so the page feels instant), the change
   is turned into database operations (diffToOps), sent to the server, and the store is then reloaded from the database.
   If the server refuses a change (a rule, a permission, a clash) the page is put back to what the database holds and the
   reason is shown.
   ===================================================================== */
import React, { useEffect, useState } from "react";

export type Val = string | number;
export type Row = { id: string; [k: string]: Val };
export type HzData = Record<string, Row[]>;

/** One operation sent to POST store/commit. */
export type HzOp = {
  src: string;
  op: "create" | "update" | "delete";
  id: string;
  rid?: number;
  row?: Record<string, Val>;
  patch?: Record<string, Val>;
  before?: Record<string, Val>;
};

/* ---------------------------------------------------------------------------------------------------------------
   configuration
   --------------------------------------------------------------------------------------------------------------- */
type Config = {
  /** Gateway address, e.g. http://localhost:5100 (no trailing slash). */
  baseUrl: string;
  /** Returns the signed-in user's bearer token. */
  token: () => string | undefined;
  /** Property to show. Leave undefined to use the first property the user can open. */
  propertyId?: number;
};

declare const process: { env?: Record<string, string | undefined> } | undefined;

/** Where the HMS gateway lives. Override with REACT_APP_HMS_API_URL (Create React App) or VITE_HMS_API_URL (Vite). */
const DEFAULT_API = "https://hms.cilpron.com";
const fromEnv = (): string | undefined => {
  try {
    const viteEnv = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
    if (viteEnv?.VITE_HMS_API_URL) return viteEnv.VITE_HMS_API_URL;
  } catch { /* not a Vite build */ }
  try {
    if (typeof process !== "undefined" && process?.env?.REACT_APP_HMS_API_URL) return process.env.REACT_APP_HMS_API_URL;
  } catch { /* no process.env in this build */ }
  return undefined;
};

let config: Config = { baseUrl: (fromEnv() ?? DEFAULT_API).replace(/\/+$/, ""), token: () => undefined };

export const hzConfigure = (c: Partial<Config>): void => {
  config = { ...config, ...c, baseUrl: (c.baseUrl ?? config.baseUrl).replace(/\/+$/, "") };
};

/** Every screen the database serves. Anything else in the store (the assistant's chat) stays in the browser. */
export const DB_SOURCES = [
  "roomtypes", "rooms", "guests", "rateplans", "reservations", "charges", "channels", "stock", "groups", "waitlist",
  "workorders", "staff", "lostfound", "feedback", "complaints", "corporates", "agents", "promos", "webbookings",
  "suppliers", "orders", "moves", "messages", "audits", "restrictions", "pricing", "audit",
] as const;

const SERVER_SOURCES = new Set<string>(DB_SOURCES);
/** Read-only on the screens: produced by the database itself. */
const READ_ONLY = new Set<string>(["audit"]);

/** Creation order, so a reservation is saved after the guest it belongs to. */
const ORDER = [
  "guests", "corporates", "agents", "suppliers", "roomtypes", "rooms", "rateplans", "stock", "promos", "channels", "groups",
  "waitlist", "reservations", "charges", "workorders", "lostfound", "complaints", "feedback", "staff", "orders", "moves",
  "messages", "restrictions", "pricing", "webbookings", "audits",
];
const rank = (src: string): number => {
  const i = ORDER.indexOf(src);
  return i < 0 ? ORDER.length : i;
};

/* ---------------------------------------------------------------------------------------------------------------
   the shared store
   --------------------------------------------------------------------------------------------------------------- */
export type HzStatus = { loading: boolean; saving: number; error: string | null; loadedAt: number | null; propertyId: number | null; denied: string[] };

const emptyStore = (): HzData => {
  const d: HzData = {};
  DB_SOURCES.forEach((s) => (d[s] = []));
  // the assistant page starts with its greeting; the conversation itself stays in the browser
  d.chat = [{ id: "M-1", role: "ai", text: "Hello. I can look at arrivals, rooms, revenue, rates, stock and guests. Ask me something, or pick a suggestion on the right." }];
  return d;
};

let store: HzData = emptyStore();
let status: HzStatus = { loading: false, saving: 0, error: null, loadedAt: null, propertyId: null, denied: [] };
const subs = new Set<() => void>();
const notify = () => subs.forEach((f) => f());
const setStatus = (patch: Partial<HzStatus>) => {
  status = { ...status, ...patch };
  notify();
};

/** The store hook the screens use. Same shape as before: [data, setData]. */
export const useHzData = (): [HzData, React.Dispatch<React.SetStateAction<HzData>>] => {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    subs.add(f);
    return () => {
      subs.delete(f);
    };
  }, []);
  return [store, setData];
};

export const useHzStatus = (): HzStatus => {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    subs.add(f);
    return () => {
      subs.delete(f);
    };
  }, []);
  return status;
};

const setData: React.Dispatch<React.SetStateAction<HzData>> = (u) => {
  const prev = store;
  const next = typeof u === "function" ? (u as (d: HzData) => HzData)(prev) : u;
  if (next === prev) return;
  store = next;
  notify();
  save(prev, next);
};

/* ---------------------------------------------------------------------------------------------------------------
   http
   --------------------------------------------------------------------------------------------------------------- */
export class HzApiError extends Error {
  /** Per-field messages when the server validated a form ({ errors: { field: message } }). */
  public fields?: Record<string, string>;
  constructor(message: string, public status: number, fields?: Record<string, string>) {
    super(message);
    this.fields = fields;
  }
}

const readError = async (res: Response): Promise<{ message: string; fields?: Record<string, string> }> => {
  try {
    const body = await res.json();
    if (body?.errors && typeof body.errors === "object") {
      const fields: Record<string, string> = {};
      Object.entries(body.errors as Record<string, unknown>).forEach(([k, v]) => (fields[k] = Array.isArray(v) ? String(v[0]) : String(v)));
      return { message: Object.values(fields).join(" "), fields };
    }
    if (typeof body?.title === "string") return { message: `${body.title} (HTTP ${res.status}${body.traceId ? `, traceId ${body.traceId}` : ""})` };
    if (typeof body?.error === "string") return { message: body.error };
    if (typeof body?.detail === "string") return { message: body.detail };
  } catch {
    /* not json */
  }
  if (res.status === 401) return { message: "Your session has expired. Sign in again." };
  if (res.status === 403) return { message: "You don't have permission to do that." };
  return { message: `The server returned ${res.status}.` };
};

export const hzFetch = async <T = unknown>(path: string, init: RequestInit = {}): Promise<T> => {
  const token = config.token();
  const res = await fetch(`${config.baseUrl}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(init.headers ?? {}) },
  });
  if (!res.ok) {
    const e = await readError(res);
    throw new HzApiError(e.message, res.status, e.fields);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
};

/* ---------------------------------------------------------------------------------------------------------------
   load
   --------------------------------------------------------------------------------------------------------------- */
const DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;

/** Server value -> screen value: numbers stay numbers, nulls become "", audit times are shown in the local format. */
export const coerce = (v: unknown): Val => {
  if (v === null || v === undefined) return "";
  if (typeof v === "number") return v;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  const s = String(v);
  if (DATE_TIME.test(s)) return new Date(`${s}Z`).toLocaleString("en-GB");
  return s;
};

export const shapeRows = (list: Array<Record<string, unknown>>): Row[] =>
  list.map((r) => {
    const o: Record<string, Val> = {};
    Object.keys(r).forEach((k) => (o[k] = coerce(r[k])));
    return o as Row;
  });

type StoreResponse = { propertyId: number; sources: Record<string, Array<Record<string, unknown>>>; denied?: string[] };

let inflight: Promise<void> | null = null;
/** One request at a time: callers that arrive while a load is running share it. */
export const loadStore = (): Promise<void> => {
  if (!inflight) inflight = loadStoreNow().finally(() => { inflight = null; });
  return inflight;
};

const loadStoreNow = async (): Promise<void> => {
  setStatus({ loading: true });
  try {
    const q = config.propertyId ? `?propertyId=${config.propertyId}` : "";
    const res = await hzFetch<StoreResponse>(`/api/dashboard/store${q}`);
    const next = emptyStore();
    Object.keys(res.sources ?? {}).forEach((k) => {
      if (SERVER_SOURCES.has(k)) next[k] = shapeRows(res.sources[k]);
    });
    next.chat = store.chat ?? [];
    store = next;
    config = { ...config, propertyId: res.propertyId };
    setStatus({ loading: false, error: null, loadedAt: Date.now(), propertyId: res.propertyId, denied: res.denied ?? [] });
  } catch (e) {
    setStatus({ loading: false, error: e instanceof Error ? e.message : "The dashboard data could not be loaded." });
  }
};

/* ---------------------------------------------------------------------------------------------------------------
   save
   --------------------------------------------------------------------------------------------------------------- */
const same = (a: Val | undefined, b: Val | undefined) => String(a ?? "") === String(b ?? "");

const strip = (r: Row): Record<string, Val> => {
  const o: Record<string, Val> = {};
  Object.keys(r).forEach((k) => {
    if (k !== "rid") o[k] = r[k];
  });
  return o;
};

/**
 * Compares the store before and after a change and returns the database operations that make the same change.
 * Where the database does something itself (accepting a web booking creates the guest and reservation, receiving an order
 * moves stock, the night audit posts the room charges) the screen's own copies of those side effects are left out,
 * otherwise they would be saved twice.
 */
export const diffToOps = (prev: HzData, next: HzData): HzOp[] => {
  const creates: HzOp[] = [];
  const updates: HzOp[] = [];
  const deletes: HzOp[] = [];

  Object.keys(next).forEach((src) => {
    if (!SERVER_SOURCES.has(src) || READ_ONLY.has(src)) return;
    const before = prev[src] ?? [];
    const after = next[src] ?? [];
    if (before === after) return;
    const old = new Map(before.map((r) => [r.id, r]));
    const now = new Map(after.map((r) => [r.id, r]));
    after.forEach((r) => {
      const o = old.get(r.id);
      if (!o) {
        creates.push({ src, op: "create", id: r.id, row: strip(r) });
        return;
      }
      if (o === r) return;
      const patch: Record<string, Val> = {};
      Object.keys(r).forEach((k) => {
        if (k !== "id" && k !== "rid" && !same(r[k], o[k])) patch[k] = r[k];
      });
      if (Object.keys(patch).length) updates.push({ src, op: "update", id: r.id, rid: typeof o.rid === "number" ? o.rid : Number(o.rid) || undefined, row: strip(r), patch, before: strip(o) });
    });
    before.forEach((o) => {
      if (!now.has(o.id)) deletes.push({ src, op: "delete", id: o.id, rid: typeof o.rid === "number" ? o.rid : Number(o.rid) || undefined, before: strip(o) });
    });
  });

  return reconcile(creates, updates, deletes, next);
};

const reconcile = (creates: HzOp[], updates: HzOp[], deletes: HzOp[], next: HzData): HzOp[] => {
  let c = creates;
  let u = updates;

  // 1. A web booking accepted: the database creates the guest and the reservation.
  if (u.some((o) => o.src === "webbookings" && o.patch?.status === "Accepted")) {
    c = c.filter((o) => o.src !== "guests" && o.src !== "reservations");
    // anything that pointed at those rows would point at nothing
    c = c.filter((o) => !(o.src === "charges" || o.src === "messages"));
  }

  // 2. An order received: the database adds the stock and writes the stock movement.
  if (u.some((o) => o.src === "orders" && o.patch?.status === "Received")) {
    c = c.filter((o) => o.src !== "moves");
  }

  // 3. Stock quantities change only through movements (receive / issue / count), never by editing the figure.
  u = u
    .map((o) => {
      if (o.src !== "stock" || !o.patch || !("qty" in o.patch)) return o;
      const { qty: _drop, ...rest } = o.patch;
      return { ...o, patch: rest };
    })
    .filter((o) => !(o.src === "stock" && o.patch && Object.keys(o.patch).length === 0));

  // 4. A room taken out of order: the database raises the work order itself, so carry the issue on the room operation.
  const oooRooms = u.filter((o) => o.src === "rooms" && o.patch?.state === "Out of order");
  if (oooRooms.length) {
    oooRooms.forEach((room) => {
      const roomNo = String(room.row?.number ?? room.id);
      const wo = c.find((o) => o.src === "workorders" && String(o.row?.room ?? "") === roomNo);
      if (wo) {
        room.patch = { ...room.patch, issue: String(wo.row?.issue ?? ""), priority: String(wo.row?.priority ?? "Medium") };
        c = c.filter((o) => o !== wo);
      }
    });
  }

  // 5. Night audit: the database posts the room charges and marks the no-shows.
  if (c.some((o) => o.src === "audits")) {
    c = c.filter((o) => !(o.src === "charges" && o.row?.dept === "Room"));
    u = u.filter((o) => !(o.src === "reservations" && o.patch?.status === "No-show"));
    c = c.filter((o) => o.src === "audits");
    u = u.filter((o) => o.src !== "reservations" && o.src !== "charges");
  }

  // 6. A new row references others by id; make sure a created reservation's guest exists in the same batch or already.
  void next;

  const byRank = (a: HzOp, b: HzOp) => rank(a.src) - rank(b.src);
  return [...c.sort(byRank), ...u.sort(byRank), ...deletes.sort((a, b) => byRank(b, a))];
};

let chain: Promise<void> = Promise.resolve();

const save = (prev: HzData, next: HzData): void => {
  const ops = diffToOps(prev, next);
  if (!ops.length) return;
  setStatus({ saving: status.saving + 1 });
  chain = chain.then(async () => {
    let failure: string | null = null;
    try {
      await hzFetch(`/api/dashboard/store/commit`, { method: "POST", body: JSON.stringify({ propertyId: config.propertyId, ops }) });
    } catch (e) {
      failure = e instanceof Error ? e.message : "The change could not be saved.";
    }
    const left = status.saving - 1;
    setStatus({ saving: left, error: failure });
    // show what the database now holds (on success the real ids and figures, on failure the old state)
    if (left === 0 || failure) await loadStore();
    if (failure) setStatus({ error: failure });
  });
};

/* ---------------------------------------------------------------------------------------------------------------
   boot hook: call once from the dashboard
   --------------------------------------------------------------------------------------------------------------- */
const REFRESH_MS = 60_000;
let bootUsers = 0;
let bootTimer = 0;
let bootFocus: (() => void) | null = null;

export const useHzBoot = (opts: { token?: string; propertyId?: number; baseUrl?: string; say?: (msg: string, err?: boolean) => void }): HzStatus => {
  const st = useHzStatus();
  const { token, propertyId, baseUrl, say } = opts;
  useEffect(() => {
    hzConfigure({ token: () => token, propertyId, ...(baseUrl ? { baseUrl } : {}) });
    void loadStore();
    // timers are shared: only the first mounted caller runs them
    bootUsers++;
    if (bootUsers === 1) {
      bootTimer = window.setInterval(() => {
        if (status.saving === 0 && !document.hidden) void loadStore();
      }, REFRESH_MS);
      bootFocus = () => {
        if (status.saving === 0) void loadStore();
      };
      window.addEventListener("focus", bootFocus);
    }
    return () => {
      bootUsers--;
      if (bootUsers === 0) {
        window.clearInterval(bootTimer);
        if (bootFocus) window.removeEventListener("focus", bootFocus);
        bootFocus = null;
      }
    };
  }, [token, propertyId, baseUrl]);
  useEffect(() => {
    if (st.error && say) say(st.error, true);
  }, [st.error]);
  return st;
};

/* ---------------------------------------------------------------------------------------------------------------
   the screens' lookups (replaces the old Property-service calls)
   --------------------------------------------------------------------------------------------------------------- */
export const hzGet = <T = unknown>(path: string): Promise<T> => hzFetch<T>(path);
export const hzPost = <T = unknown>(path: string, body: unknown): Promise<T> => hzFetch<T>(path, { method: "POST", body: JSON.stringify(body) });
export const hzPropertyId = (): number | undefined => config.propertyId;

/** Lets a screen that calls its own endpoint (offers, room form) ask the shared store to re-read the database. */
export const hzReload = (): Promise<void> => loadStore();

/* ---------------------------------------------------------------------------------------------------------------
   legacy lookups
   The reservation form and the pickers used to call the Property service (Rooms/autocomplete, Guests/autocomplete,
   RoomCategory, RoomType/{id}, Reservations/arrivals). dashApiCall keeps the same call shape, sends each one to the
   dashboard service, and returns the same field names the form already reads. Anything that is not a dashboard lookup
   (for example Tenant/search on the auth service) goes to the original apiCall.
   --------------------------------------------------------------------------------------------------------------- */
type LegacyCall = { service: string; endpoint: string; method?: string; headers?: Record<string, string>; data?: unknown };
type Items<T> = { items?: T[] } | T[];
const listOf = <T,>(r: Items<T>): T[] => (Array.isArray(r) ? r : r?.items ?? []);
const pid = () => (config.propertyId ? `propertyId=${config.propertyId}` : "");
const joinQ = (...parts: string[]) => {
  const q = parts.filter(Boolean).join("&");
  return q ? `?${q}` : "";
};
const nameParts = (name: string): { firstName: string; lastName: string } => {
  const [first, ...rest] = (name || "").trim().split(/\s+/);
  return { firstName: first ?? "", lastName: rest.join(" ") };
};

export const makeDashApiCall = (fallback: (c: LegacyCall) => Promise<any>) => async (call: LegacyCall): Promise<any> => {
  if (call.service === "auth") return fallback(call);
  const [path, query = ""] = call.endpoint.split("?");
  const params = new URLSearchParams(query);
  const get = (k: string) => params.get(k) ?? "";
  try {
    switch (true) {
      case path === "Guests/autocomplete": {
        const rows = await hzGet<Array<Record<string, unknown>>>(`/api/dashboard/guests/autocomplete${joinQ(`query=${encodeURIComponent(get("query"))}`)}`);
        return rows.map((r) => ({ ...r, guestId: r.id, ...nameParts(String(r.name ?? "")), fullName: r.name }));
      }
      case path === "Rooms/autocomplete": {
        const rows = await hzGet<Array<Record<string, unknown>>>(`/api/dashboard/rooms/autocomplete${joinQ(pid(), `query=${encodeURIComponent(get("query"))}`)}`);
        return rows.map((r) => ({ ...r, roomId: r.id, roomNumber: r.roomNo, roomTypeName: r.roomType, roomType: r.roomTypeId }));
      }
      case path === "RoomCategory": {
        const rows = listOf(await hzGet<Items<Record<string, unknown>>>(`/api/dashboard/sections/categories${joinQ(pid(), `q=${encodeURIComponent(get("query"))}`, "pageSize=100")}`));
        return rows.map((r) => ({ ...r, categoryId: r.rid ?? r.id, categoryName: r.name, categoryDescription: r.description ?? "" }));
      }
      case path.startsWith("RoomType/"): {
        const id = Number(decodeURIComponent(path.slice("RoomType/".length)));
        const rows = listOf(await hzGet<Items<Record<string, unknown>>>(`/api/dashboard/sections/roomtypes${joinQ(pid(), "pageSize=200")}`));
        const r = rows.find((x) => Number(x.rid) === id);
        return r ? { ...r, id: r.rid, roomTypeId: r.rid, roomTypeName: r.name, capacity: r.maxOcc, rate: r.base } : null;
      }
      case path === "Reservations/arrivals":
        return await hzGet(`/api/dashboard/reservations/arrivals${joinQ(pid(), get("date") ? `date=${get("date").slice(0, 10)}` : "")}`);
      case path === "Reservations" && (call.method ?? "GET").toUpperCase() === "POST":
        return await hzPost(`/api/dashboard/reservations`, call.data);
      default:
        // Workflow tabs and the hotel search have no counterpart in the database: an empty list makes the callers use the live store.
        return [];
    }
  } catch (e) {
    return { error: e instanceof Error ? e.message : "The request failed." };
  }
};